import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/* --- BIOS boot log --- */
const LINES = [
  { t: "BIOSS v4.20 LOADING...", d: 120 },
  { t: "MEMORY CHECK ................. 640K OK", d: 60 },
  { t: "DETECTING TELEMETRY INTERFACE ...", d: 100 },
  { t: "TELEMETRY LINK ............... [ ONLINE ]", d: 60 },
  { t: "SYNCING NORAD CATALOG ........ 28,451 OBJECTS", d: 100 },
  { t: "CONNECTING TO DEEP SPACE NETWORK ...", d: 120 },
  { t: "DSN GOLDSTONE ................ LOCKED", d: 55 },
  { t: "DSN CANBERRA ................. LOCKED", d: 55 },
  { t: "DSN MADRID ................... LOCKED", d: 55 },
  { t: "MOUNTING /MISSION_CONTROL .... OK", d: 100 },
  { t: "LOADING PILOT PROFILE ........ OK", d: 60 },
  { t: "INITIALISING ASTRONATLAS TRANSMISSIONS ...", d: 200 },
  { t: "> READY.", d: 240 },
];

/* --- Web-Audio boot chord: longer, louder, more satisfying ---
   Layered composition: (1) noise "power-on" click, (2) sub-thump,
   (3) swelling fifth chord with filter sweep, (4) resolving high chime,
   (5) warm major-2nd overtone tail. All synthesised — zero asset files.
   Respects the global SFX toggle and the shared ambient volume slider. */
function playBootChord() {
  try {
    if (localStorage.getItem("sat_click_sfx_on") === "0") return null;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    const userVol = parseFloat(localStorage.getItem("sat_boot_vol"));
    const VOL = Number.isFinite(userVol) ? Math.min(1, Math.max(0, userVol)) : 0.7;
    if (VOL < 0.02) return null;
    const ctx = new AC();
    const t0 = ctx.currentTime;
    const T = 6.4;                     // total duration ← longer, no clip
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, t0);
    master.gain.exponentialRampToValueAtTime(VOL, t0 + 0.55);
    master.gain.setValueAtTime(VOL, t0 + T - 1.6);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + T);
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(180, t0);
    filter.frequency.exponentialRampToValueAtTime(2800, t0 + 1.6);
    filter.frequency.exponentialRampToValueAtTime(1400, t0 + T);
    filter.Q.value = 6;
    // Feedback delay for space-shimmer tail
    const delay = ctx.createDelay(2);
    delay.delayTime.value = 0.42;
    const fb = ctx.createGain();
    fb.gain.value = 0.35;
    const wet = ctx.createGain();
    wet.gain.value = 0.32;
    filter.connect(delay);
    delay.connect(fb).connect(delay);
    delay.connect(wet).connect(master);
    filter.connect(master);
    master.connect(ctx.destination);

    // 1) Power-on click — 40ms filtered noise burst
    const buf = ctx.createBuffer(1, ctx.sampleRate * 0.05, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const ngain = ctx.createGain();
    ngain.gain.setValueAtTime(0.7, t0);
    ngain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.05);
    noise.connect(ngain).connect(filter);
    noise.start(t0);

    // (Low sub-thump removed on user request — it read as an unpleasant "brief low pitch sound".)

    // 3) Swelling fifth chord — A2, E3, A3 (saw+saw+sine)
    [110, 165, 220].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = i === 2 ? "sine" : "sawtooth";
      o.detune.value = i === 1 ? -6 : (i === 2 ? +8 : 0);
      o.frequency.setValueAtTime(freq * 0.5, t0);
      o.frequency.exponentialRampToValueAtTime(freq, t0 + 1.0);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime([0.34, 0.24, 0.20][i], t0 + 0.9);
      g.gain.setValueAtTime([0.34, 0.24, 0.20][i], t0 + T - 2.0);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + T - 0.05);
      o.connect(g).connect(filter);
      o.start(t0); o.stop(t0 + T);
    });

    // 4) Resolving chime — 880 → 1320 Hz bell (major fifth) at t=1.9s
    const chime = ctx.createOscillator();
    const chimeG = ctx.createGain();
    chime.type = "sine";
    chime.frequency.setValueAtTime(880, t0 + 1.9);
    chime.frequency.exponentialRampToValueAtTime(1320, t0 + 2.4);
    chimeG.gain.setValueAtTime(0.0001, t0 + 1.9);
    chimeG.gain.exponentialRampToValueAtTime(0.36, t0 + 2.2);
    chimeG.gain.exponentialRampToValueAtTime(0.0001, t0 + T - 0.4);
    chime.connect(chimeG).connect(master);
    chime.start(t0 + 1.9); chime.stop(t0 + T);

    // 5) Warm major-2nd overtone tail at t=3.6s — soft resolve
    [330, 440].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.detune.value = i === 0 ? 0 : -4;
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, t0 + 3.6);
      g.gain.exponentialRampToValueAtTime(i === 0 ? 0.20 : 0.14, t0 + 4.4);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + T - 0.15);
      o.connect(g).connect(filter);
      o.start(t0 + 3.6); o.stop(t0 + T);
    });

    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, (T + 0.5) * 1000);
    return { ctx, master };
  } catch { return null; }
}

const KEY = "sat_boot_v1";

export default function BootSequence() {
  const [phase, setPhase] = useState(() => {
    if (typeof window === "undefined") return "done";
    if (sessionStorage.getItem(KEY) === "1") return "done";
    return "bios";
  });
  const [shown, setShown] = useState(0);
  const [cursor, setCursor] = useState(true);
  const [vol, setVol] = useState(() => {
    const raw = parseFloat(localStorage.getItem("sat_boot_vol"));
    return Number.isFinite(raw) ? Math.min(1, Math.max(0, raw)) : 0.7;
  });
  const timers = useRef([]);
  const chordPlayed = useRef(false);
  const chordHandle = useRef(null);

  // Manual replay trigger from the SFX panel.
  useEffect(() => {
    const replay = () => {
      sessionStorage.removeItem(KEY);
      chordPlayed.current = false;
      setShown(0);
      setPhase("bios");
    };
    window.addEventListener("sat:boot-replay", replay);
    return () => window.removeEventListener("sat:boot-replay", replay);
  }, []);

  useEffect(() => {
    if (phase !== "bios") return;
    // Stream BIOS lines one after another.
    let acc = 0;
    LINES.forEach((_, i) => {
      acc += LINES[i - 1]?.d ?? 250;
      const id = setTimeout(() => setShown(i + 1), acc);
      timers.current.push(id);
    });
    // After the last line, glitch → logo → shatter → done.
    const bootDone = acc + LINES[LINES.length - 1].d + 300;
    const toLogo = setTimeout(() => setPhase("logo"), bootDone);
    timers.current.push(toLogo);
    // Cursor blink
    const blink = setInterval(() => setCursor((c) => !c), 480);
    timers.current.push(blink);
    return () => {
      timers.current.forEach((t) => { clearTimeout(t); clearInterval(t); });
      timers.current = [];
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== "logo") return;
    if (!chordPlayed.current) { chordHandle.current = playBootChord(); chordPlayed.current = true; }
    const toShatter = setTimeout(() => setPhase("shatter"), 5400);
    const done = setTimeout(() => {
      sessionStorage.setItem(KEY, "1");
      setPhase("done");
    }, 6400);
    timers.current.push(toShatter, done);
    return () => timers.current.forEach((t) => clearTimeout(t));
  }, [phase]);

  const onVol = (e) => {
    const v = parseFloat(e.target.value);
    setVol(v);
    localStorage.setItem("sat_boot_vol", String(v));
    // Live-update if the chord is currently ringing
    try {
      const h = chordHandle.current;
      if (h?.master && h.ctx) {
        h.master.gain.cancelScheduledValues(h.ctx.currentTime);
        h.master.gain.setValueAtTime(v, h.ctx.currentTime);
      }
    } catch { /* ignore */ }
  };

  const skip = () => {
    // Kill the chord immediately when the user bails
    try {
      const h = chordHandle.current;
      if (h?.ctx) {
        h.master?.gain?.cancelScheduledValues(h.ctx.currentTime);
        h.master?.gain?.exponentialRampToValueAtTime(0.0001, h.ctx.currentTime + 0.25);
        setTimeout(() => { try { h.ctx.close(); } catch { /* ignore */ } }, 300);
      }
    } catch { /* ignore */ }
    sessionStorage.setItem(KEY, "1");
    setPhase("done");
  };

  if (phase === "done") return null;

  const node = (
    <div
      className={`sat-boot ${phase === "shatter" ? "sat-boot--shatter" : ""}`}
      data-testid="boot-sequence"
      aria-hidden="true"
    >
      {/* Horizontal slices used only by the shatter animation */}
      {[0,1,2,3,4,5,6,7].map((i) => (
        <div key={i} className="sat-boot__slice" style={{ "--i": i }} />
      ))}

      <div className="sat-boot__crt">
        {phase === "bios" && (
          <>
            <pre className="sat-boot__log" data-testid="boot-log">
{LINES.slice(0, shown).map((l, i) => (
  <div key={i}>{l.t}</div>
))}
              <span className="sat-boot__cursor">{cursor ? "█" : " "}</span>
            </pre>
            <button
              type="button"
              onClick={skip}
              data-testid="boot-skip"
              data-sfx-silent
              className="sat-boot__skip"
            >
              [ ESC → SKIP ]
            </button>
          </>
        )}

        {phase === "logo" && (
          <div className="sat-boot__logo" data-testid="boot-logo">
            <svg viewBox="0 0 120 120" width="88" height="88">
              <defs>
                <radialGradient id="bg" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.85"/>
                  <stop offset="70%" stopColor="#22d3ee" stopOpacity="0.15"/>
                  <stop offset="100%" stopColor="#22d3ee" stopOpacity="0"/>
                </radialGradient>
              </defs>
              <circle cx="60" cy="60" r="34" fill="url(#bg)" />
              <circle cx="60" cy="60" r="8" fill="#22d3ee" />
              <ellipse cx="60" cy="60" rx="52" ry="14" stroke="#22d3ee" strokeWidth="1.5" fill="none" transform="rotate(-18 60 60)"/>
              <ellipse cx="60" cy="60" rx="52" ry="14" stroke="#22d3ee" strokeWidth="1" fill="none" transform="rotate(48 60 60)" opacity="0.6"/>
              <ellipse cx="60" cy="60" rx="52" ry="14" stroke="#22d3ee" strokeWidth="1" fill="none" transform="rotate(112 60 60)" opacity="0.35"/>
            </svg>
            <h1 className="sat-boot__title">ASTRONATLAS</h1>
            <div className="sat-boot__tag">TRANSMISSIONS · v2.0</div>
          </div>
        )}
      </div>

      {/* Persistent volume + skip controls */}
      <div className="sat-boot__vol" data-testid="boot-vol-wrap">
        <span className="sat-boot__vol-label">CHORD</span>
        <input
          type="range" min="0" max="1" step="0.01" value={vol}
          onChange={onVol}
          data-testid="boot-vol"
          data-sfx-silent
          aria-label="Boot chord volume"
          className="sat-boot__vol-slider"
          style={{ ["--fill"]: `${Math.round(vol * 100)}%` }}
        />
        <span className="sat-boot__vol-val">{Math.round(vol * 100)}</span>
        {phase === "logo" && (
          <button
            type="button"
            onClick={skip}
            data-testid="boot-skip"
            data-sfx-silent
            className="sat-boot__skip sat-boot__skip--inline"
          >
            [ SKIP ]
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
